import React from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import Svg, { Circle, G, Path, Rect } from 'react-native-svg';
import { colors } from '../../theme';

/**
 * Line-icon set drawn to match the Figma mockups: 24x24 grid, single weight,
 * round caps and joins, no fills except where a glyph is deliberately solid
 * (`homeFilled`, `starFilled`, `more`).
 *
 * All glyphs are hand-authored here rather than pulled from an icon font so
 * the stroke weight stays consistent with the design at every size.
 */

type GlyphProps = {
  color: string;
  strokeWidth: number;
};

const stroke = (color: string, strokeWidth: number) => ({
  stroke: color,
  strokeWidth,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  fill: 'none' as const,
});

const glyphs = {
  // ---------------------------------------------------------------- interface
  search: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={11} cy={11} r={7} {...stroke(color, strokeWidth)} />
      <Path d="M16.3 16.3 21 21" {...stroke(color, strokeWidth)} />
    </>
  ),
  sliders: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path d="M3 7h18M3 12h18M3 17h18" {...stroke(color, strokeWidth)} />
      <Circle cx={9} cy={7} r={2.1} {...stroke(color, strokeWidth)} fill={colors.background} />
      <Circle cx={15} cy={12} r={2.1} {...stroke(color, strokeWidth)} fill={colors.background} />
      <Circle cx={7} cy={17} r={2.1} {...stroke(color, strokeWidth)} fill={colors.background} />
    </>
  ),
  bell: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M18 15.2v-4.4a6 6 0 1 0-12 0v4.4L4.4 17.8h15.2L18 15.2Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M9.9 20.4a2.3 2.3 0 0 0 4.2 0" {...stroke(color, strokeWidth)} />
    </>
  ),
  pin: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M12 21.2s7-6.2 7-11.2a7 7 0 1 0-14 0c0 5 7 11.2 7 11.2Z"
        {...stroke(color, strokeWidth)}
      />
      <Circle cx={12} cy={10} r={2.6} {...stroke(color, strokeWidth)} />
    </>
  ),
  calendar: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect x={3.5} y={5} width={17} height={15.5} rx={3} {...stroke(color, strokeWidth)} />
      <Path d="M3.5 10h17M8 2.8v4.4M16 2.8v4.4" {...stroke(color, strokeWidth)} />
    </>
  ),
  /*
   * Solid counterpart to `calendar`, for the selected tab. The header band and
   * the two hangers stay in the body colour and the punched-out days are cut
   * with `fillRule="evenodd"`, so the glyph reads as the same object filled in
   * rather than a different one.
   */
  calendarFilled: ({ color }: GlyphProps) => (
    <>
      <Path d="M8 2.8v3M16 2.8v3" stroke={color} strokeWidth={2} strokeLinecap="round" />
      <Path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.5 8.5h17V17.5a3 3 0 0 1-3 3H6.5a3 3 0 0 1-3-3V8.5Zm3 3.2h2.4v2.2H6.5v-2.2Zm4.3 0h2.4v2.2h-2.4v-2.2Zm4.3 0h2.4v2.2h-2.4v-2.2Zm-8.6 4.1h2.4v2.2H6.5v-2.2Zm4.3 0h2.4v2.2h-2.4v-2.2Z"
        fill={color}
      />
      <Path
        d="M3.5 8.5V8a3 3 0 0 1 3-3h11a3 3 0 0 1 3 3v.5h-17Z"
        fill={color}
      />
    </>
  ),
  clock: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={12} cy={12} r={8.5} {...stroke(color, strokeWidth)} />
      <Path d="M12 7.2V12l3.4 2" {...stroke(color, strokeWidth)} />
    </>
  ),
  /**
   * Stopwatch - a held slot running out, not the passage of time.
   *
   * `clock` already carries "when is it", so the expiry screens needed a
   * second face. The crown and the side button are what separate the two at a
   * glance; without them this is `clock` with a nub on top.
   */
  stopwatch: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={12} cy={13.6} r={7.4} {...stroke(color, strokeWidth)} />
      <Path d="M12 9.8v3.8l2.6 1.6" {...stroke(color, strokeWidth)} />
      <Path d="M9.6 3.2h4.8M12 3.2v3" {...stroke(color, strokeWidth)} />
      <Path d="m18.6 7 1.5-1.5" {...stroke(color, strokeWidth)} />
    </>
  ),
  /**
   * Circular arrow - retrying a payment, and the refund on its way back.
   *
   * The arc deliberately stops short of a full circle: closed, it reads as a
   * loading spinner, which is the one thing a "Try Again" button must not say.
   */
  refresh: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M20.2 12a8.2 8.2 0 1 1-2.6-6"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M20.4 4.4v4.4H16" {...stroke(color, strokeWidth)} />
    </>
  ),
  /** Failure. The counterpart to `checkCircle`, drawn on the same circle. */
  xCircle: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={12} cy={12} r={8.6} {...stroke(color, strokeWidth)} />
      <Path d="m9.2 9.2 5.6 5.6M14.8 9.2l-5.6 5.6" {...stroke(color, strokeWidth)} />
    </>
  ),
  /**
   * QR code - scan and pay, and the mark on the UPI method row.
   *
   * Three finder squares plus a scatter of modules. A literal code would be
   * unreadable at 18pt and dishonest at any size, since it would not scan.
   */
  qr: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect x={3.4} y={3.4} width={7} height={7} rx={1.6} {...stroke(color, strokeWidth)} />
      <Rect x={13.6} y={3.4} width={7} height={7} rx={1.6} {...stroke(color, strokeWidth)} />
      <Rect x={3.4} y={13.6} width={7} height={7} rx={1.6} {...stroke(color, strokeWidth)} />
      <Path
        d="M13.6 13.6h3.2M20.6 13.6v3.2M13.6 17.6v3M17.6 20.6h3"
        {...stroke(color, strokeWidth)}
      />
    </>
  ),
  shieldCheck: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M12 2.8 4.8 5.6v6c0 4.5 3 8.4 7.2 9.6 4.2-1.2 7.2-5.1 7.2-9.6v-6L12 2.8Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="m8.9 11.8 2.3 2.4 4-4.6" {...stroke(color, strokeWidth)} />
    </>
  ),
  lock: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect x={4.5} y={10} width={15} height={10.5} rx={2.8} {...stroke(color, strokeWidth)} />
      <Path d="M8.2 10V7.6a3.8 3.8 0 0 1 7.6 0V10" {...stroke(color, strokeWidth)} />
    </>
  ),
  phone: ({ color, strokeWidth }: GlyphProps) => (
    <Path
      d="M6.3 3.6h2.9l1.5 4-2 1.4a12.6 12.6 0 0 0 6.3 6.3l1.4-2 4 1.5v2.9a1.8 1.8 0 0 1-2 1.8C11.3 19.7 4.3 12.7 4.5 5.6a1.8 1.8 0 0 1 1.8-2Z"
      {...stroke(color, strokeWidth)}
    />
  ),
  /** Solid counterpart to `phone`, for the selected tab. */
  phoneFilled: ({ color }: GlyphProps) => (
    <Path
      d="M6.3 3.6h2.9l1.5 4-2 1.4a12.6 12.6 0 0 0 6.3 6.3l1.4-2 4 1.5v2.9a1.8 1.8 0 0 1-2 1.8C11.3 19.7 4.3 12.7 4.5 5.6a1.8 1.8 0 0 1 1.8-2Z"
      fill={color}
    />
  ),
  /**
   * Hang up - the same handset as `phone`, laid down.
   *
   * Rotated rather than redrawn, and that is the whole idea: end-call is not a
   * different object from call, it is the same handset put back. Every dialler
   * draws it this way, and reusing the path means the two glyphs cannot drift
   * apart when one of them is tweaked.
   *
   * 135 degrees keeps the whole path inside the 24pt box - the furthest point
   * of the handset sits about 10.2 from the centre, so nothing clips.
   */
  phoneEnd: ({ color, strokeWidth }: GlyphProps) => (
    <G rotation={135} origin="12, 12">
      <Path
        d="M6.3 3.6h2.9l1.5 4-2 1.4a12.6 12.6 0 0 0 6.3 6.3l1.4-2 4 1.5v2.9a1.8 1.8 0 0 1-2 1.8C11.3 19.7 4.3 12.7 4.5 5.6a1.8 1.8 0 0 1 1.8-2Z"
        {...stroke(color, strokeWidth)}
      />
    </G>
  ),
  /**
   * A call arriving - the handset, with an arrow coming down into it.
   *
   * The arrow sits in the top-right quadrant, which the handset path leaves
   * empty: it runs as a band from the top-left corner down to the
   * bottom-right, so there is nothing there to collide with and the glyph
   * needs no scaling to make room.
   */
  phoneIncoming: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M6.3 3.6h2.9l1.5 4-2 1.4a12.6 12.6 0 0 0 6.3 6.3l1.4-2 4 1.5v2.9a1.8 1.8 0 0 1-2 1.8C11.3 19.7 4.3 12.7 4.5 5.6a1.8 1.8 0 0 1 1.8-2Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M21 3.2 15.8 8.4" {...stroke(color, strokeWidth)} />
      <Path d="M15.8 4.9v3.5h3.5" {...stroke(color, strokeWidth)} />
    </>
  ),
  /** The microphone, live. Capsule, cradle, stand - the studio shape. */
  mic: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect x={9} y={2.8} width={6} height={11} rx={3} {...stroke(color, strokeWidth)} />
      <Path
        d="M5.6 11.4a6.4 6.4 0 0 0 12.8 0M12 17.8V21M8.8 21h6.4"
        {...stroke(color, strokeWidth)}
      />
    </>
  ),
  /**
   * Muted. The same microphone with a single stroke through it.
   *
   * A slash rather than a hollowed-out capsule: at 18pt inside a round control
   * the difference between a filled and an unfilled mic is invisible, while a
   * line across it reads as "off" at any size and in any colour.
   */
  micOff: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect x={9} y={2.8} width={6} height={11} rx={3} {...stroke(color, strokeWidth)} />
      <Path
        d="M5.6 11.4a6.4 6.4 0 0 0 12.8 0M12 17.8V21M8.8 21h6.4"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M4.2 3.4 19.8 19" {...stroke(color, strokeWidth)} />
    </>
  ),
  /**
   * The loudspeaker, for routing a call out of the earpiece.
   *
   * Two arcs rather than three. Three is the volume icon, and this control is
   * not a volume control - it is a two-state routing switch, and borrowing the
   * volume glyph for it invites somebody to look for a slider.
   */
  speaker: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M11.6 4.4 6.9 8.5H3.9v7h3l4.7 4.1V4.4Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M15.2 9.5a3.6 3.6 0 0 1 0 5" {...stroke(color, strokeWidth)} />
      <Path d="M17.9 6.9a7.2 7.2 0 0 1 0 10.2" {...stroke(color, strokeWidth)} />
    </>
  ),
  /**
   * Speech bubble with a tail. Rounded rectangle rather than a rounded
   * rectangle plus a separate triangle - one path means the tail joins the
   * body with no seam showing at small sizes.
   */
  chat: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M20.5 12.4a7.6 7.6 0 0 1-8.2 7.6 8.8 8.8 0 0 1-2.9-.5L4.6 21l1.3-4a7.2 7.2 0 0 1-1.4-4.3 7.6 7.6 0 0 1 8-7.6 7.6 7.6 0 0 1 8 7.3Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M9 12.4h6" {...stroke(color, strokeWidth)} />
      <Path d="M9 15.4h3.4" {...stroke(color, strokeWidth)} />
    </>
  ),
  /**
   * Solid counterpart, for the selected tab. The two message lines are punched
   * out with `fillRule="evenodd"`, so the bar behind shows through them and the
   * glyph stays a bubble rather than becoming a blob.
   */
  chatFilled: ({ color }: GlyphProps) => (
    <Path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.5 12.4a7.6 7.6 0 0 1-8.2 7.6 8.8 8.8 0 0 1-2.9-.5L4.6 21l1.3-4a7.2 7.2 0 0 1-1.4-4.3 7.6 7.6 0 0 1 8-7.6 7.6 7.6 0 0 1 8 7.3ZM9 11.4h6v1.9H9v-1.9Zm0 3.1h3.4v1.9H9v-1.9Z"
      fill={color}
    />
  ),
  star: ({ color, strokeWidth }: GlyphProps) => (
    <Path
      d="m12 3.2 2.7 5.6 6.1.9-4.4 4.3 1.1 6.1L12 17.2 6.5 20.1l1.1-6.1-4.4-4.3 6.1-.9L12 3.2Z"
      {...stroke(color, strokeWidth)}
    />
  ),
  starFilled: ({ color }: GlyphProps) => (
    <Path
      d="m12 3.2 2.7 5.6 6.1.9-4.4 4.3 1.1 6.1L12 17.2 6.5 20.1l1.1-6.1-4.4-4.3 6.1-.9L12 3.2Z"
      fill={color}
    />
  ),
  /** Two ticks - "delivered and read", the way every messenger draws it. */
  checkDouble: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path d="m2 12.4 4 4L14.4 8" {...stroke(color, strokeWidth)} />
      <Path d="m10.6 16.4 7.4-7.4" {...stroke(color, strokeWidth)} />
      <Path d="M15.6 6.6 22 13" {...stroke(color, strokeWidth)} />
    </>
  ),
  /** Headset - support, as distinct from a professional you booked. */
  headset: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M4 13v-1a8 8 0 0 1 16 0v1"
        {...stroke(color, strokeWidth)}
      />
      <Path
        d="M4 13h2.2a.8.8 0 0 1 .8.8v3.4a.8.8 0 0 1-.8.8H5.4A1.4 1.4 0 0 1 4 16.6V13Zm16 0h-2.2a.8.8 0 0 0-.8.8v3.4a.8.8 0 0 0 .8.8h1.4a1.4 1.4 0 0 0 1.4-1.4V13Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M20 18v.6a2.4 2.4 0 0 1-2.4 2.4H13" {...stroke(color, strokeWidth)} />
    </>
  ),
  check: ({ color, strokeWidth }: GlyphProps) => (
    <Path d="m4.5 12.5 5 5 10-11" {...stroke(color, strokeWidth)} />
  ),
  plus: ({ color, strokeWidth }: GlyphProps) => (
    <Path d="M12 5v14M5 12h14" {...stroke(color, strokeWidth)} />
  ),
  minus: ({ color, strokeWidth }: GlyphProps) => (
    <Path d="M5 12h14" {...stroke(color, strokeWidth)} />
  ),
  chevronRight: ({ color, strokeWidth }: GlyphProps) => (
    <Path d="m9.5 5 7 7-7 7" {...stroke(color, strokeWidth)} />
  ),
  /** Long arrow with a shaft - the onboarding "next" affordance, not a chevron. */
  arrowRight: ({ color, strokeWidth }: GlyphProps) => (
    <Path d="M3.6 12h16.8m-6.6-6.6L20.4 12l-6.6 6.6" {...stroke(color, strokeWidth)} />
  ),
  /** `arrowRight` mirrored - a back affordance with a shaft, not a chevron. */
  arrowLeft: ({ color, strokeWidth }: GlyphProps) => (
    <Path d="M20.4 12H3.6m6.6-6.6L3.6 12l6.6 6.6" {...stroke(color, strokeWidth)} />
  ),
  chevronLeft: ({ color, strokeWidth }: GlyphProps) => (
    <Path d="m14.5 5-7 7 7 7" {...stroke(color, strokeWidth)} />
  ),
  chevronDown: ({ color, strokeWidth }: GlyphProps) => (
    <Path d="m5 9.5 7 7 7-7" {...stroke(color, strokeWidth)} />
  ),
  /*
   * "Pull me up" - the hint on a bar that opens, not a navigation chevron.
   *
   * A curve rather than `chevronDown` flipped. The angular pair are controls
   * you press; this one is never pressed and only ever says which way the
   * sheet goes, and the softer arc reads as that rather than as a button
   * somebody forgot to make tappable.
   */
  pullUp: ({ color, strokeWidth }: GlyphProps) => (
    <Path d="M5 14.5Q12 5 19 14.5" {...stroke(color, strokeWidth)} />
  ),
  more: ({ color }: GlyphProps) => (
    <>
      <Circle cx={5.2} cy={12} r={1.7} fill={color} />
      <Circle cx={12} cy={12} r={1.7} fill={color} />
      <Circle cx={18.8} cy={12} r={1.7} fill={color} />
    </>
  ),
  /** Tick inside a ring - a completed count, not the bare tick. */
  checkCircle: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={12} cy={12} r={8.6} {...stroke(color, strokeWidth)} />
      <Path d="m8.2 12.3 2.6 2.6 5-5.4" {...stroke(color, strokeWidth)} />
    </>
  ),
  /**
   * Gear.
   *
   * A ring plus eight radial teeth rather than one toothed outline: the real
   * silhouette needs sixteen alternating arcs, which turns to mush below about
   * 20pt. This keeps the read at any size.
   */
  settings: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={12} cy={12} r={7} {...stroke(color, strokeWidth)} />
      <Circle cx={12} cy={12} r={2.8} {...stroke(color, strokeWidth)} />
      {[0, 45, 90, 135].map(angle => (
        <G key={angle} rotation={angle} origin="12, 12">
          <Path d="M12 2.6v2.2M12 19.2v2.2" {...stroke(color, strokeWidth)} />
        </G>
      ))}
    </>
  ),
  /** Billfold with a clasp - the wallet balance. */
  wallet: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect x={3} y={6} width={18} height={13.5} rx={3} {...stroke(color, strokeWidth)} />
      <Path d="M3 10.5h13a2 2 0 0 1 0 4H3" {...stroke(color, strokeWidth)} />
      <Circle cx={16.4} cy={12.5} r={1} fill={color} />
    </>
  ),
  /** Two figures - referring someone, rather than one account. */
  users: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={9.4} cy={8.4} r={3.4} {...stroke(color, strokeWidth)} />
      <Path d="M3.2 20.2a6.2 6.2 0 0 1 12.4 0" {...stroke(color, strokeWidth)} />
      <Path
        d="M16 5.4a3.4 3.4 0 0 1 0 6M17.4 14.6a6.2 6.2 0 0 1 3.4 5.6"
        {...stroke(color, strokeWidth)}
      />
    </>
  ),
  /** Price tag with its eyelet - offers and coupons. */
  tag: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M20.8 12.6 12.4 21a2 2 0 0 1-2.8 0L3 14.4a2 2 0 0 1 0-2.8L11.4 3.2h7.4a2 2 0 0 1 2 2v7.4Z"
        {...stroke(color, strokeWidth)}
      />
      <Circle cx={16.4} cy={7.6} r={1.5} {...stroke(color, strokeWidth)} />
    </>
  ),
  /** Wrapped box with a bow - the offer banners' artwork. */
  gift: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect x={3.2} y={8.4} width={17.6} height={4.4} rx={1.4} {...stroke(color, strokeWidth)} />
      <Path d="M4.8 12.8v6.6a2 2 0 0 0 2 2h10.4a2 2 0 0 0 2-2v-6.6" {...stroke(color, strokeWidth)} />
      <Path d="M12 8.4v13" {...stroke(color, strokeWidth)} />
      <Path
        d="M12 8.4S10.8 3 8.4 3a2.4 2.4 0 0 0 0 5.4M12 8.4S13.2 3 15.6 3a2.4 2.4 0 0 1 0 5.4"
        {...stroke(color, strokeWidth)}
      />
    </>
  ),
  /** Three nodes joined by two links - the platform-neutral share mark. */
  share: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={18} cy={5.5} r={2.6} {...stroke(color, strokeWidth)} />
      <Circle cx={6} cy={12} r={2.6} {...stroke(color, strokeWidth)} />
      <Circle cx={18} cy={18.5} r={2.6} {...stroke(color, strokeWidth)} />
      <Path d="m8.3 10.8 7.4-4M8.3 13.2l7.4 4" {...stroke(color, strokeWidth)} />
    </>
  ),
  /** Door with an arrow leaving it. */
  logout: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M14.5 4.5H6.8A2.3 2.3 0 0 0 4.5 6.8v10.4a2.3 2.3 0 0 0 2.3 2.3h7.7"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M15.8 8.4 19.5 12l-3.7 3.6M19.5 12H9.6" {...stroke(color, strokeWidth)} />
    </>
  ),
  /** Bin - removing a line from the cart outright. */
  trash: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path d="M4 6.5h16" {...stroke(color, strokeWidth)} />
      <Path
        d="M9.5 6.5V5a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 14.5 5v1.5"
        {...stroke(color, strokeWidth)}
      />
      <Path
        d="M6.5 6.5v12A1.5 1.5 0 0 0 8 20h8a1.5 1.5 0 0 0 1.5-1.5v-12"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M10.2 10.2v6M13.8 10.2v6" {...stroke(color, strokeWidth)} />
    </>
  ),
  cart: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M2.6 3.4h2.6l2.3 10.9h9.6l2.3-7.6H6.2"
        {...stroke(color, strokeWidth)}
      />
      <Circle cx={9.4} cy={19} r={1.6} {...stroke(color, strokeWidth)} />
      <Circle cx={16.6} cy={19} r={1.6} {...stroke(color, strokeWidth)} />
    </>
  ),
  play: ({ color, strokeWidth }: GlyphProps) => (
    <Path d="M8.4 5.2 18.6 12 8.4 18.8V5.2Z" {...stroke(color, strokeWidth)} />
  ),
  playFilled: ({ color }: GlyphProps) => (
    <Path d="M8.4 5.2 18.6 12 8.4 18.8V5.2Z" fill={color} />
  ),
  heart: ({ color, strokeWidth }: GlyphProps) => (
    <Path
      d="M12 20.4 4.9 13.3a4.6 4.6 0 0 1 6.5-6.5l.6.6.6-.6a4.6 4.6 0 1 1 6.5 6.5L12 20.4Z"
      {...stroke(color, strokeWidth)}
    />
  ),
  scissors: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={6.2} cy={6.2} r={2.6} {...stroke(color, strokeWidth)} />
      <Circle cx={6.2} cy={17.8} r={2.6} {...stroke(color, strokeWidth)} />
      <Path d="M8.4 7.8 19.5 18.9M19.5 5.1 8.4 16.2" {...stroke(color, strokeWidth)} />
    </>
  ),
  grid: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect x={3.4} y={3.4} width={7} height={7} rx={2} {...stroke(color, strokeWidth)} />
      <Rect x={13.6} y={3.4} width={7} height={7} rx={2} {...stroke(color, strokeWidth)} />
      <Rect x={3.4} y={13.6} width={7} height={7} rx={2} {...stroke(color, strokeWidth)} />
      <Rect x={13.6} y={13.6} width={7} height={7} rx={2} {...stroke(color, strokeWidth)} />
    </>
  ),
  leaf: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M20 4c0 8.3-4.4 13.2-10.5 13.2A5.5 5.5 0 0 1 4 11.7C4 6.4 10.6 4.6 20 4Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M4.5 20.5C7 15 12 10.8 17.5 8.6" {...stroke(color, strokeWidth)} />
    </>
  ),

  // ---------------------------------------------------------------- tab bar
  home: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M3.8 10.4 12 3.6l8.2 6.8V19a2 2 0 0 1-2 2H5.8a2 2 0 0 1-2-2v-8.6Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M9.4 21v-6.1h5.2V21" {...stroke(color, strokeWidth)} />
    </>
  ),
  /*
   * Solid house with the door punched out rather than drawn on.
   *
   * `fillRule="evenodd"` makes the second subpath a hole, so whatever is behind
   * the glyph shows through it - the white bar in the tab strip. Painting a
   * white door on top instead would only look right against white, and would
   * show as a white smear the moment the icon sits on the dome marker or on any
   * tinted surface.
   */
  homeFilled: ({ color }: GlyphProps) => (
    <Path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.8 10.4 12 3.6l8.2 6.8V19a2 2 0 0 1-2 2H5.8a2 2 0 0 1-2-2v-8.6ZM10.2 21v-4.6a1.8 1.8 0 0 1 3.6 0V21h-3.6Z"
      fill={color}
    />
  ),
  /**
   * A house with a bookmark standing in the doorway - the bookings mark.
   *
   * The doorway is left open at the foot rather than closed off, so the ribbon
   * reads as standing in it rather than as a shape floating inside a box. Its
   * notch drops below the house's baseline for the same reason: overlapping
   * the edge is what makes two outlines read as one object instead of as a
   * sticker on a wall.
   *
   * Curves rather than mitred corners at the eaves and the ridge, because
   * `strokeLinejoin: round` alone barely softens a 1.6pt join - at 22pt the
   * corners came back sharp against the rest of the set.
   */
  homeBookmark: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M7.8 20.4H5.4Q3.6 20.4 3.6 18.6V11.6Q3.6 10.4 4.5 9.6L10.7 4.1Q12 3 13.3 4.1L19.5 9.6Q20.4 10.4 20.4 11.6V18.6Q20.4 20.4 18.6 20.4H16.2"
        {...stroke(color, strokeWidth)}
      />
      <Path
        d="M9 21.6V14.2Q9 12.6 10.6 12.6H13.4Q15 12.6 15 14.2V21.6L12 18.9Z"
        {...stroke(color, strokeWidth)}
      />
    </>
  ),
  target: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={12} cy={12} r={8} {...stroke(color, strokeWidth)} />
      <Circle cx={12} cy={12} r={2.6} {...stroke(color, strokeWidth)} />
      <Path d="M12 1.8v2.6M12 19.6v2.6M1.8 12h2.6M19.6 12h2.6" {...stroke(color, strokeWidth)} />
    </>
  ),
  creditCard: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect x={2.5} y={5} width={19} height={14} rx={3} {...stroke(color, strokeWidth)} />
      <Path d="M2.5 10h19M6 14.8h3.5" {...stroke(color, strokeWidth)} />
    </>
  ),
  user: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={12} cy={8} r={3.7} {...stroke(color, strokeWidth)} />
      <Path d="M4.8 20.6a7.2 7.2 0 0 1 14.4 0" {...stroke(color, strokeWidth)} />
    </>
  ),

  /** Solid counterpart to `user`, for the selected tab. */
  userFilled: ({ color }: GlyphProps) => (
    <>
      <Circle cx={12} cy={8} r={3.7} fill={color} />
      <Path d="M4.8 20.6a7.2 7.2 0 0 1 14.4 0Z" fill={color} />
    </>
  ),

  // --------------------------------------------------------------- services
  /**
   * Trigger spray bottle with mist. Reads as "cleaning" faster than a broom
   * at tile size - a broom's bristles collapse into a blob below ~28pt.
   */
  sprayBottle: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M9 8.5h5a2.5 2.5 0 0 1 2.5 2.5v8a2.5 2.5 0 0 1-2.5 2.5H9a2.5 2.5 0 0 1-2.5-2.5v-8A2.5 2.5 0 0 1 9 8.5Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M6.5 12.5h10" {...stroke(color, strokeWidth)} />
      <Path d="M10 8.5V5.5h3.2v3" {...stroke(color, strokeWidth)} />
      <Path d="M10 6.4 6.2 5" {...stroke(color, strokeWidth)} />
      <Path
        d="M4.6 3.1 2.9 2.3M4.2 5.2 2.3 5.3M4.6 7.3 2.9 8"
        {...stroke(color, strokeWidth)}
      />
    </>
  ),
  /** Wall-mounted tap with a drop. Plumbing, rather than a generic wrench. */
  faucet: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path d="M4.5 21h7.5" {...stroke(color, strokeWidth)} />
      <Path d="M8.2 21v-9.4" {...stroke(color, strokeWidth)} />
      <Path
        d="M8.2 11.6h4.6a4.6 4.6 0 0 1 4.6 4.6v1.4"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M8.2 11.6V8.4M6 8.4h4.4" {...stroke(color, strokeWidth)} />
      <Circle cx={17.4} cy={20.4} r={1.5} {...stroke(color, strokeWidth)} />
    </>
  ),
  /** Two-pin plug with a lead. More literal than a bolt for "electrical". */
  plug: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path d="M8.6 2.8v4.4M15.4 2.8v4.4" {...stroke(color, strokeWidth)} />
      <Path
        d="M5.8 7.2h12.4v4.6a6.2 6.2 0 0 1-12.4 0V7.2Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M12 18v3.4" {...stroke(color, strokeWidth)} />
    </>
  ),
  broom: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path d="M19 3.5 11.6 11" {...stroke(color, strokeWidth)} />
      <Path
        d="M7.2 14.5 10.6 11l4.4 4.4-3.5 3.4a2 2 0 0 1-2.8 0l-1.5-1.5a2 2 0 0 1 0-2.8Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M8 19.5 6 22M11 20.6 9.8 22.6M13.8 19 13 21.6" {...stroke(color, strokeWidth)} />
    </>
  ),
  wrench: ({ color, strokeWidth }: GlyphProps) => (
    <Path
      d="M20.6 6.3a4.7 4.7 0 0 1-6.2 6.2l-7.9 7.9a2 2 0 0 1-2.9-2.9l7.9-7.9a4.7 4.7 0 0 1 6.2-6.2l-3 3 2.9 2.9 3-3Z"
      {...stroke(color, strokeWidth)}
    />
  ),
  bolt: ({ color, strokeWidth }: GlyphProps) => (
    <Path
      d="M13.2 2.2 4.6 13.6h6.1l-1 8.2 8.7-11.4h-6.1l.9-8.2Z"
      {...stroke(color, strokeWidth)}
    />
  ),
  washer: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect x={4} y={2.5} width={16} height={19} rx={3.2} {...stroke(color, strokeWidth)} />
      <Circle cx={12} cy={14} r={4.6} {...stroke(color, strokeWidth)} />
      <Path d="M4 7.6h16" {...stroke(color, strokeWidth)} />
      <Circle cx={7.6} cy={5} r={0.9} fill={color} />
      <Circle cx={10.6} cy={5} r={0.9} fill={color} />
    </>
  ),
  hairDryer: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M3.6 9a5 5 0 0 1 5-5H16a5 5 0 0 1 0 10H8.6a5 5 0 0 1-5-5Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M8.9 14v3.6a2.4 2.4 0 0 0 4.8 0V14" {...stroke(color, strokeWidth)} />
      <Path d="M20.4 9h1.9" {...stroke(color, strokeWidth)} />
    </>
  ),
  bug: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M7.5 12a4.5 4.5 0 0 1 9 0v2.6a4.5 4.5 0 0 1-9 0V12Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M9.4 7.8a2.6 2.6 0 0 1 5.2 0" {...stroke(color, strokeWidth)} />
      <Path d="M9.6 5.4 8.2 3M14.4 5.4 15.8 3" {...stroke(color, strokeWidth)} />
      <Path
        d="M7.5 11 4.4 9.6M7.5 14H4.4M7.5 16.8 5 18.6M16.5 11l3.1-1.4M16.5 14h3.1M16.5 16.8l2.5 1.8"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M12 8.6v10" {...stroke(color, strokeWidth)} />
    </>
  ),
  hammer: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M9.4 6.6 13.5 2.5l8 8-4.1 4.1-2.6-2.6"
        {...stroke(color, strokeWidth)}
      />
      <Path
        d="M11.6 8.8 3.9 16.5a2 2 0 0 0 0 2.8l1.2 1.2a2 2 0 0 0 2.8 0l7.7-7.7"
        {...stroke(color, strokeWidth)}
      />
    </>
  ),
  paintRoller: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect x={3} y={4} width={12} height={5.5} rx={1.8} {...stroke(color, strokeWidth)} />
      <Path
        d="M15 6.75h3.6a1.8 1.8 0 0 1 1.8 1.8v3a1.8 1.8 0 0 1-1.8 1.8h-6a1.8 1.8 0 0 0-1.8 1.8v.6"
        {...stroke(color, strokeWidth)}
      />
      <Rect x={9} y={15.5} width={3.6} height={5.5} rx={1.4} {...stroke(color, strokeWidth)} />
    </>
  ),
  // ------------------------------------------------------ after the job
  /* Adding a photo to a review, and the tile that opens the picker. */
  camera: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M4 8.4h3.2L8.8 6h6.4l1.6 2.4H20a1.4 1.4 0 0 1 1.4 1.4v8A1.4 1.4 0 0 1 20 19.2H4a1.4 1.4 0 0 1-1.4-1.4v-8A1.4 1.4 0 0 1 4 8.4Z"
        {...stroke(color, strokeWidth)}
      />
      <Circle cx={12} cy={13.4} r={3.4} {...stroke(color, strokeWidth)} />
    </>
  ),
  /* A photo already attached - the thumbnail's own fallback. */
  image: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Rect
        x={3}
        y={5}
        width={18}
        height={14}
        rx={2}
        {...stroke(color, strokeWidth)}
      />
      <Circle cx={8.6} cy={9.6} r={1.5} {...stroke(color, strokeWidth)} />
      <Path d="M4 16.6 9.4 12l4 3.4 2.6-2 4 3.4" {...stroke(color, strokeWidth)} />
    </>
  ),
  /* The invoice, and any other document the app hands over. */
  document: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M6 3.6h7.2L18.6 9v11.4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4.6a1 1 0 0 1 1-1Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M13 3.8V9h5.2" {...stroke(color, strokeWidth)} />
      <Path d="M8.4 13h7M8.4 16.4h4.6" {...stroke(color, strokeWidth)} />
    </>
  ),
  /* Something needs attention - a dispute, a failed refund, a cancellation. */
  alert: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Path
        d="M12 4.2 21 19.4H3L12 4.2Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M12 10.2v4.2" {...stroke(color, strokeWidth)} />
      <Circle cx={12} cy={16.8} r={0.9} fill={color} />
    </>
  ),
  /*
   * The alarm. A bell inside a ring rather than a plain bell, because it has
   * to read as a different object from the notifications glyph beside it -
   * one of these opens a list, the other calls for help.
   */
  siren: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={12} cy={12} r={9} {...stroke(color, strokeWidth)} />
      <Path
        d="M15.4 13.6V11a3.4 3.4 0 1 0-6.8 0v2.6L7.4 15.4h9.2l-1.2-1.8Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M10.8 17.4a1.6 1.6 0 0 0 2.4 0" {...stroke(color, strokeWidth)} />
    </>
  ),
  /* Money coming back - an arrow turning out of a coin. */
  refund: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={12} cy={12} r={8.6} {...stroke(color, strokeWidth)} />
      <Path
        d="M15 9.4H9.6a2.4 2.4 0 0 0 0 4.8h3.6"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M11.4 12 9 14.2l2.4 2.2" {...stroke(color, strokeWidth)} />
    </>
  ),
  /*
   * The ₹ sign, drawn as a single continuous stroke path rather than the
   * literal character (which renders at whatever weight the system font
   * gives it, not matched to the hand-drawn glyphs around it) and rather
   * than several separate `Path` elements (the earlier drafts overlapped
   * their own strokes at 14-19px and read as a knot, or as "≤").
   *
   * One path, traced the way the letterform is actually written: down the
   * left stem, across the top bar, back down and across the second bar,
   * then the single diagonal tail out to the bottom-right. Every segment
   * connects to the last, so there is nothing for two strokes to cross.
   */
  rupee: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={12} cy={12} r={8.6} {...stroke(color, strokeWidth)} />
      <Path
        d="M9.4 7.6h5.2M9.4 7.6v1.7M9.4 7.6h1.4a2 2 0 0 1 0 4H9.4M9.4 9.6h5.2M9.4 11.6h1.4L14.6 16.4"
        {...stroke(color, strokeWidth)}
      />
    </>
  ),
  fan: ({ color, strokeWidth }: GlyphProps) => (
    <>
      <Circle cx={12} cy={12} r={8.6} {...stroke(color, strokeWidth)} />
      {[0, 120, 240].map(angle => (
        <G key={angle} rotation={angle} origin="12, 12">
          <Path
            d="M12 10.4c-2.7-1.4-3.9-3.3-2.9-5.1.8-1.4 2.5-1.6 3.7-.8 1.9 1.2 2 3.8 1.2 5.9"
            {...stroke(color, strokeWidth)}
          />
        </G>
      ))}
      <Circle cx={12} cy={12} r={1.5} fill={color} />
    </>
  ),
  /**
   * WhatsApp's own mark - a bubble with a handset in it.
   *
   * The one glyph in this set that is not drawn in the house style: it is
   * someone else's logo, and a hand-drawn approximation of a logo is the
   * version people do not recognise. Filled, because the mark is solid; it
   * takes `color` like everything else, so it can be drawn in WhatsApp green
   * on a light chip or in white on a filled button.
   *
   * Two subpaths in one `Path`: the handset, then the ring and its tail.
   */
  whatsapp: ({ color }: GlyphProps) => (
    <Path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"
      fill={color}
    />
  ),
} as const;

export type IconName = keyof typeof glyphs;

/** Every glyph name, handy for icon-picker screens and tests. */
export const iconNames = Object.keys(glyphs) as IconName[];

export type IconProps = {
  name: IconName;
  /** Rendered box, in points. Defaults to 24 - the design grid. */
  size?: number;
  /** Stroke / fill colour. Defaults to the primary brand green. */
  color?: string;
  /**
   * Stroke weight on the 24pt grid. The mockups use a light 1.6.
   * Scaled automatically so a 16pt icon keeps the same optical weight.
   */
  strokeWidth?: number;
  style?: StyleProp<ViewStyle>;
};

function Icon({
  name,
  size = 24,
  color = colors.primary,
  strokeWidth = 1.6,
  style,
}: IconProps) {
  const Glyph = glyphs[name];

  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" style={style}>
      <Glyph color={color} strokeWidth={strokeWidth} />
    </Svg>
  );
}

/*
 * Memoised, and it matters more than it looks.
 *
 * Every glyph is a `react-native-svg` tree - a `Svg` plus one to four `Path`s
 * - and this is the most-repeated component in the app: three to a service
 * row, five in the tab bar, two in every list heading. Without this, one
 * keystroke in a search field rebuilt every SVG node on screen.
 *
 * All its props are primitives, so the comparison is a handful of `===` and
 * almost always skips. A caller passing an inline `style` object defeats it
 * for that one instance, which is no worse than the previous behaviour.
 */
export default React.memo(Icon);
